import * as z from 'zod';
import { validationMessagesMap } from 'src/utils/messages';
import { PAYMENT_TYPE, PRICING_MODE, PRICING_MODEL } from 'src/features/servicePlanPricingPolicies/utils/types';

const servicePlanPricingPolicyBaseSchema = z.object({
    valid_from: z.string(),
    payment_type: z.enum(PAYMENT_TYPE),
    valid_to: z.string().optional().nullable(),
    tax_reason: z.string(),
    pricing_model: z.enum(PRICING_MODEL),
    free_quantities: z.number().int().min(0).optional().nullable(),
    /** UI-only discriminator; stripped before the payload is sent. */
    pricing_mode: z.enum(PRICING_MODE),
    amount: z.number().int().min(0).optional().nullable(),
    reference_rate: z.string().optional().nullable(),
    rate_multiplier: z.string().optional().nullable(),
});

export type ServicePlanPricingPolicyFormSchemaValues = z.infer<typeof servicePlanPricingPolicyBaseSchema>;

/** What actually goes over the wire - the mode is a form concern, not an API field. */
export type ServicePlanPricingPolicyPayload = Omit<ServicePlanPricingPolicyFormSchemaValues, 'pricing_mode'>;

export type PatchServicePlanPricingPolicyFormSchemaValues = Partial<ServicePlanPricingPolicyPayload>;

/*
 * The mode decides which side is required. Sending both, or neither, is a 400 - the modal builds the
 * payload from the mode so only one side can ever leave the form.
 */
export const servicePlanPricingPolicyFormSchema = servicePlanPricingPolicyBaseSchema.superRefine((values, ctx) => {
    if (values.pricing_mode === PRICING_MODE.FIXED) {
        if (values.amount == null) {
            ctx.addIssue({
                path: ['amount'],
                code: 'custom',
                message: validationMessagesMap.validationErrorRequired,
            });
        }
        return;
    }

    if (!values.reference_rate) {
        ctx.addIssue({
            path: ['reference_rate'],
            code: 'custom',
            message: validationMessagesMap.validationErrorRequired,
        });
    }

    const multiplier = Number(values.rate_multiplier);
    if (!values.rate_multiplier || Number.isNaN(multiplier) || multiplier <= 0) {
        ctx.addIssue({
            path: ['rate_multiplier'],
            code: 'custom',
            message: validationMessagesMap.validationErrorMinValue,
        });
    }
});
